import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../../utils/api';
import { TZ_SHORT, getCourseScheduleEntries, sortEntriesByDayOrder } from '../../utils/courseSchedule';
import SupportChat from './SupportChat';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const STRIPE_APPEARANCE = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#3F51B5',
    borderRadius: '8px',
    fontFamily: 'system-ui, -apple-system, sans-serif'
  }
};

const TYPE_LABELS = {
  long_course: 'Long Program',
  block: 'Short Program',
  workshop: 'Workshop'
};

const LOCATION_LABELS = {
  online: 'Online',
  in_person: 'In-Person',
  hybrid: 'Hybrid'
};

/* ─── Inner payment form (must be inside <Elements>) ──────────── */
const CheckoutForm = ({ enrollmentId, totalAmount, onSuccess, onError, tcsRequired, tcsAccepted }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [showBankWarning, setShowBankWarning] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: 'if_required'
    });

    if (error) {
      // Show bank account warning if the error is related to bank linking
      if (error.message?.toLowerCase().includes('bank') ||
          error.message?.toLowerCase().includes('financial') ||
          error.message?.toLowerCase().includes('connect') ||
          error.type === 'validation_error') {
        setShowBankWarning(true);
      }
      onError(error.message);
      setProcessing(false);
    } else if (paymentIntent) {
      if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
        const isProcessing = paymentIntent.status === 'processing';
        try {
          const { data } = await api.post('/enroll/confirm-payment', { enrollmentId });
          onSuccess({ ...data, processing: isProcessing });
        } catch (err) {
          if (isProcessing) {
            // Bank transfer: payment is still processing, webhook will handle enrollment
            onSuccess({ processing: true });
          } else {
            // Card payment succeeded but confirm API failed — webhook will handle
            onSuccess({ processing: false });
          }
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {showBankWarning && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 font-medium">Bank linking failed</p>
          <p className="text-xs text-red-600 mt-1">
            Please switch to the <strong>Card</strong> tab above and pay with a <strong>credit card</strong> (not debit) to complete your enrollment.
          </p>
        </div>
      )}

      <PaymentElement options={{ layout: 'tabs' }} />
      <button
        type="submit"
        disabled={!stripe || processing || (tcsRequired && !tcsAccepted)}
        className="w-full mt-6 bg-brand-500 text-white py-3.5 rounded-lg text-lg font-semibold hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {processing ? 'Processing payment...' : `Enroll & Pay $${totalAmount}`}
      </button>
      {tcsRequired && !tcsAccepted && (
        <p className="mt-2 text-xs text-amber-700 text-center">Please agree to the Terms &amp; Conditions above to enable payment.</p>
      )}
    </form>
  );
};

/* ─── T&C checkbox row + modal — rendered when studio has T&Cs configured ── */
const TCsCheckboxRow = ({ studioName, accepted, onChange, onView }) => (
  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={accepted}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 text-brand-500 border-gray-300 rounded focus:ring-brand-400 cursor-pointer"
      />
      <span className="text-sm text-gray-800 leading-relaxed">
        I have read and agree to {studioName ? <strong>{studioName}'s</strong> : 'the studio’s'} Terms &amp; Conditions.{' '}
        <button
          type="button"
          onClick={onView}
          className="text-brand-600 hover:text-brand-700 underline font-medium"
        >
          View Terms &amp; Conditions
        </button>
      </span>
    </label>
  </div>
);

const TCsModal = ({ open, onClose, studioName, tcs }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-3 py-6"
      role="dialog"
      aria-modal="true"
      aria-label={`${studioName || 'Studio'} Terms and Conditions`}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-base font-semibold text-gray-900 truncate pr-4">
            {studioName ? `${studioName} — ` : ''}Terms &amp; Conditions
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden="true">
              <path d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div
            className="text-sm text-gray-800 leading-relaxed font-serif"
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {tcs || 'No Terms & Conditions configured.'}
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium bg-brand-500 hover:bg-brand-600 text-white rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Main enrollment page ─────────────────────────────────────── */
const EnrollmentPage = () => {
  const { slug, courseId } = useParams();
  const { isAuthenticated, login, user, refreshUser } = useAuth();

  // Data
  const [studioData, setStudioData] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // null = nothing picked yet. We intentionally don't auto-select the first
  // plan — that surfaced a race where, for a returning signed-in user with a
  // saved PM, the payment UI wouldn't render until the user manually changed
  // plan and forced a re-render. Single-plan courses auto-select themselves
  // in a useEffect below, so those users see zero extra friction.
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Auth
  const authLoading = false; // redirect-based auth, no loading state needed

  // Payment
  const [clientSecret, setClientSecret] = useState(null);
  const [enrollmentId, setEnrollmentId] = useState(null);
  const [enrollmentTotal, setEnrollmentTotal] = useState(null);
  const [isEnrollmentWithAdvance, setIsEnrollmentWithAdvance] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  // Parental login: dependents + which profile is being enrolled
  const [dependents, setDependents] = useState([]);
  const [myActiveEnrollments, setMyActiveEnrollments] = useState([]); // both self + dependents
  const [selectedStudentId, setSelectedStudentId] = useState(null);   // null = pick required
  const [addingChild, setAddingChild] = useState(false);
  const [newChildName, setNewChildName] = useState('');
  const [addingChildError, setAddingChildError] = useState('');
  const [addingChildSaving, setAddingChildSaving] = useState(false);

  // Saved payment method (card/bank ending in XXXX) — enables one-click enroll
  const [savedPaymentMethod, setSavedPaymentMethod] = useState(null);
  const [useSavedPm, setUseSavedPm] = useState(true); // default ON when a saved PM exists
  const [oneClickEnrolling, setOneClickEnrolling] = useState(false);

  // T&Cs — gates both the saved-PM one-click path and the new-card CheckoutForm.
  // Only enforced when the studio has configured non-empty T&Cs in Settings → T&Cs.
  const [tcsAccepted, setTcsAccepted] = useState(false);
  const [tcsModalOpen, setTcsModalOpen] = useState(false);
  const tcsRequired = !!(studioData?.tcs && studioData.tcs.trim());

  // Discount code state — local to this page; passed to create-payment / enroll-with-saved-pm.
  const [discountAccordionOpen, setDiscountAccordionOpen] = useState(false);
  const [discountInput, setDiscountInput] = useState('');
  const [discountApplying, setDiscountApplying] = useState(false);
  const [discountError, setDiscountError] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, amountOff, scope }

  /* ── Fetch course data ── */
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const { data } = await api.get(`/widget/${slug}/courses`);
        setStudioData(data.studio);
        const found = data.courses.find(c => c._id === courseId);
        if (found) {
          setCourse(found);
        } else {
          setError('Course not found or not currently available.');
        }
      } catch (err) {
        setError('Studio not found.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourse();
  }, [slug, courseId]);

  /* ── Parental login: fetch dependents + who's already enrolled in THIS course ──
   * Only runs when the user is authenticated. Single-student users get
   * dependents=[] and myActiveEnrollments scoped to themselves — the picker
   * will automatically hide and flow defaults to self-enroll.
   */
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const [meRes, myEnrollRes, pmRes] = await Promise.all([
          api.get('/auth/me'),
          api.get('/enroll/my'),
          api.get('/enroll/current-payment-method').catch(() => ({ data: { paymentMethod: null } }))
        ]);
        if (cancelled) return;
        setDependents(meRes.data.dependents || []);
        // Treat both 'active' and 'processing' (ACH bank transfers settling) as
        // "this profile has paid and shouldn't appear as re-enrollable".
        const active = (myEnrollRes.data.enrollments || []).filter(e =>
          e.status === 'active' || e.status === 'processing'
        );
        setMyActiveEnrollments(active);
        setSavedPaymentMethod(pmRes.data.paymentMethod || null);
      } catch (e) {
        // non-fatal: picker just won't show, user falls back to self-enroll
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // Compute "who can enroll in this course right now" — filtered profiles.
  // A profile is eligible if they are NOT already actively enrolled in THIS course.
  const profiles = useMemo(() => {
    if (!user) return [];
    // Map studentId → array of { courseId, courseName } for all of their active
    // enrollments at this studio. Used to (a) flag them as already enrolled in
    // the current course and (b) surface other courses they're already in.
    const enrollmentsByStudent = new Map();
    for (const e of myActiveEnrollments) {
      const sid = (e.student?._id || e.student || '').toString();
      if (!sid) continue;
      const cid = (e.course?._id || e.course || '').toString();
      const cname = e.course?.name || '';
      if (!enrollmentsByStudent.has(sid)) enrollmentsByStudent.set(sid, []);
      enrollmentsByStudent.get(sid).push({ courseId: cid, courseName: cname });
    }
    const buildProfile = (id, name, isSelf) => {
      const idStr = id?.toString();
      const enrolls = enrollmentsByStudent.get(idStr) || [];
      const currentEnrollment = enrolls.find(c => c.courseId === String(courseId));
      return {
        _id: id,
        name,
        isSelf,
        alreadyEnrolled: !!currentEnrollment,
        currentCourseName: currentEnrollment?.courseName || '',
        otherEnrollments: enrolls.filter(c => c.courseId !== String(courseId)),
      };
    };
    const self = buildProfile(user.id || user._id, user.name || user.email, true);
    const children = dependents.map(d => buildProfile(d._id, d.name, false));
    return [self, ...children];
  }, [user, dependents, myActiveEnrollments, courseId]);

  // Default-select the first eligible profile after data loads
  useEffect(() => {
    if (selectedStudentId || profiles.length === 0) return;
    const firstEligible = profiles.find(p => !p.alreadyEnrolled);
    if (firstEligible) setSelectedStudentId(firstEligible._id);
  }, [profiles, selectedStudentId]);

  const handleAddChild = async (e) => {
    e.preventDefault();
    if (!newChildName.trim()) {
      setAddingChildError('Name is required');
      return;
    }
    setAddingChildSaving(true);
    setAddingChildError('');
    try {
      const { data } = await api.post('/auth/dependents', { name: newChildName.trim() });
      const newDep = data.dependent;
      setDependents(prev => [...prev, newDep]);
      // Use the canonical profile-switch handler so the new child inherits
      // the saved-PM one-click block (or a fresh Elements form), not stale
      // state from whoever was selected before. Without this, clientSecret /
      // useSavedPm leftovers can hide the payment method block on the new pick.
      handleProfileChange(newDep._id);
      setNewChildName('');
      setAddingChild(false);
    } catch (err) {
      setAddingChildError(err.response?.data?.error || 'Could not add family member');
    } finally {
      setAddingChildSaving(false);
    }
  };

  // Show the picker whenever the user is authenticated. Even first-time enrollers
  // need the "+ Add a family member" affordance — otherwise the only path to add
  // a dependent is buried in the dashboard and parents miss it during signup.
  const showPicker = isAuthenticated && profiles.length > 0;

  // If the course has exactly one billing plan, there's nothing for the user
  // to choose — auto-select it. Multi-plan courses stay null until the user
  // explicitly picks monthly/quarterly/etc.
  useEffect(() => {
    if (course?.billingPlans?.length === 1 && selectedPlan === null) {
      setSelectedPlan(0);
    }
  }, [course, selectedPlan]);

  // Set page title + favicon based on studio
  useEffect(() => {
    if (studioData?.name) {
      document.title = studioData.name;
    }
    if (studioData?.branding?.logo) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = studioData.branding.logo;
      document.head.appendChild(link);
    }
  }, [studioData]);

  /* ── Create payment intent ──
   *
   * Important: the guard here is a ref, not `creatingPayment` state, because
   * React batches setState but not synchronous effect re-entry. Using a state
   * flag in the effect dependency list creates a re-render loop that can fire
   * POST /enroll/create-payment hundreds of times (Swati incident 2026-04-19).
   * The ref blocks synchronous re-entry; once the request completes the ref
   * is cleared whether or not the server returned a clientSecret (error paths
   * included), so the user can retry after selecting a different plan.
   */
  const inFlightRef = useRef(false);

  const createPaymentIntent = useCallback(async () => {
    if (!course) return;
    if (selectedPlan === null || selectedPlan === undefined) return; // wait for plan pick
    if (!selectedStudentId) return;   // wait until profile is picked
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setCreatingPayment(true);
    setError('');
    try {
      const { data } = await api.post('/enroll/create-payment', {
        courseId: course._id,
        billingPlanIndex: selectedPlan,
        studentId: selectedStudentId,
        discountCode: appliedDiscount?.code || undefined,
        tcsAccepted: tcsAccepted || undefined
      });
      setClientSecret(data.clientSecret);
      setEnrollmentId(data.enrollmentId);
      if (data.isEnrollmentWithAdvance) {
        setEnrollmentTotal(data.enrollmentTotal);
        setIsEnrollmentWithAdvance(true);
      }
    } catch (err) {
      // If the server rejected the discount at this stage (e.g. exhausted), drop it
      // so the user can proceed at full price by retrying.
      if (err.response?.status === 409 || /discount/i.test(err.response?.data?.error || '')) {
        setAppliedDiscount(null);
      }
      setError(err.response?.data?.error || 'Failed to initialize payment');
    } finally {
      setCreatingPayment(false);
      inFlightRef.current = false;
    }
  }, [course, selectedPlan, selectedStudentId, appliedDiscount, tcsAccepted]);

  // Auto-create payment intent when authenticated AND a profile is picked.
  // Skip when the user has a saved PM and hasn't opted out — in that case
  // we render a one-click button instead of Elements, and only fall back
  // to PI creation if the one-click charge fails or the user requests a new card.
  // Deps intentionally exclude `createPaymentIntent` and `creatingPayment`:
  // their churn triggered the render-loop that caused duplicate PIs in prod.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const canOneClick = !!savedPaymentMethod && useSavedPm;
    if (canOneClick) return;
    if (selectedPlan === null || selectedPlan === undefined) return; // wait for plan pick
    if (tcsRequired && !tcsAccepted) return; // wait for T&Cs acceptance — backend gates anyway
    if (isAuthenticated && course && selectedStudentId && !clientSecret && !paymentSuccess && !inFlightRef.current) {
      createPaymentIntent();
    }
  }, [isAuthenticated, course, selectedStudentId, clientSecret, paymentSuccess, selectedPlan, savedPaymentMethod, useSavedPm, tcsRequired, tcsAccepted]);

  const handleOneClickEnroll = async () => {
    if (!course || !selectedStudentId) return;
    if (tcsRequired && !tcsAccepted) return;
    if (selectedPlan === null || selectedPlan === undefined) return;
    setOneClickEnrolling(true);
    setError('');
    try {
      const { data } = await api.post('/enroll/enroll-with-saved-pm', {
        courseId: course._id,
        billingPlanIndex: selectedPlan,
        studentId: selectedStudentId,
        discountCode: appliedDiscount?.code || undefined,
        tcsAccepted: tcsAccepted || undefined
      });
      if (data.success) {
        setEnrollmentId(data.enrollmentId);
        setPaymentSuccess(true);
        // Re-pull the customer's default PM so a follow-up enrollment for
        // another family member in the same session can one-click too.
        refreshSavedPaymentMethod();
        setTimeout(async () => { try { await refreshUser(); } catch (e) { /* ok */ } }, 1500);
      }
    } catch (err) {
      // If the saved PM failed, fall back to Elements by toggling off useSavedPm.
      // The auto-create effect will then fire and show the card form.
      if (err.response?.data?.requiresNewPaymentMethod || err.response?.data?.noSavedPaymentMethod) {
        setUseSavedPm(false);
        setError(err.response.data.error || 'Please enter a new payment method');
      } else {
        setError(err.response?.data?.error || 'Enrollment failed');
      }
    } finally {
      setOneClickEnrolling(false);
    }
  };

  /* ── Auth handlers (Clerk modal, no redirect needed) ── */
  const handleSignUp = () => {
    sessionStorage.setItem('dancehall_signup_role', 'student');
    login({ mode: 'signup', studioName: studioData?.name });
  };

  const handleLogin = () => {
    sessionStorage.setItem('dancehall_signup_role', 'student');
    login({ studioName: studioData?.name });
  };

  /* ── Discount-code apply / remove ── */
  const handleApplyDiscount = async () => {
    const code = String(discountInput || '').trim().toUpperCase();
    if (!code) return;
    setDiscountApplying(true);
    setDiscountError('');
    try {
      const { data } = await api.post('/enroll/validate-code', {
        studioId: studioData?._id,
        courseId: course._id,
        code
      });
      if (!data.valid) {
        const reasonMap = {
          expired: 'This code has expired.',
          exhausted: 'This code has reached its redemption limit.',
          disabled: 'Discount codes are currently unavailable.',
          not_found: 'Code not found.'
        };
        setDiscountError(reasonMap[data.reason] || 'Invalid code.');
        return;
      }
      setAppliedDiscount({
        code: data.displayCode || code,
        amountOff: data.amountOff,
        scope: data.scope
      });
      setDiscountInput('');
      // Reset any in-flight payment so the next intent uses the discount
      setClientSecret(null);
      setEnrollmentId(null);
      setEnrollmentTotal(null);
      setIsEnrollmentWithAdvance(false);
    } catch (err) {
      setDiscountError(err.response?.data?.error || 'Failed to validate code.');
    } finally {
      setDiscountApplying(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountError('');
    // Reset payment so a fresh PI is created without the discount
    setClientSecret(null);
    setEnrollmentId(null);
    setEnrollmentTotal(null);
    setIsEnrollmentWithAdvance(false);
  };

  /* ── Profile change resets payment state so the picked student gets either
   *   the saved-PM one-click block or a fresh PI for Elements. Also re-opens
   *   the saved PM (in case the previous student opted for a different card)
   *   and clears the inFlightRef so a re-entry isn't blocked.
   */
  const handleProfileChange = (id) => {
    setSelectedStudentId(id);
    setClientSecret(null);
    setEnrollmentId(null);
    setEnrollmentTotal(null);
    setIsEnrollmentWithAdvance(false);
    setUseSavedPm(true);
    setError('');
    inFlightRef.current = false;
  };

  /* ── Re-pull the saved payment method on demand. Called after a successful
   *   first enrollment so the parent can one-click subsequent kids without a
   *   page refresh — Stripe attaches the card to the customer at charge time,
   *   but our client state was loaded before that happened.
   */
  const refreshSavedPaymentMethod = useCallback(async () => {
    try {
      const { data } = await api.get('/enroll/current-payment-method');
      setSavedPaymentMethod(data.paymentMethod || null);
    } catch (e) {
      // non-fatal — user can still pay via Elements
    }
  }, []);

  /* ── Plan change resets payment + discount ── */
  const handlePlanChange = (idx) => {
    setSelectedPlan(idx);
    if (clientSecret) {
      setClientSecret(null);
      setEnrollmentId(null);
      setEnrollmentTotal(null);
      setIsEnrollmentWithAdvance(false);
    }
    // The new plan may not support discounts (e.g. sub-no-advance). Drop it
    // and let the user re-apply if applicable.
    if (appliedDiscount) {
      setAppliedDiscount(null);
      setDiscountError('');
    }
  };

  /* ── Payment callbacks ── */
  const handlePaymentSuccess = async (data) => {
    if (data?.processing) {
      setPaymentProcessing(true);
    }
    setPaymentSuccess(true);
    // The card just got attached to the Stripe customer — pull it back so a
    // subsequent family-member enrollment in the same session shows the
    // saved-PM one-click block instead of forcing the card form again.
    refreshSavedPaymentMethod();
    // Refresh user data so studioId is set (webhook may take a moment)
    setTimeout(async () => {
      try { await refreshUser(); } catch (e) { /* ok */ }
    }, 2000);
  };

  const handlePaymentError = (message) => {
    setError(message);
  };

  /* ── Loading / Error states ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading course...</div>
      </div>
    );
  }

  if (error && !course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-2">Not Available</h2>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const plan = course.billingPlans?.[selectedPlan];
  const studentPrice = plan?.studentPrice || plan?.amount || 0;
  // Amount actually due today: for subscription-with-advance plans it's the
  // enrollment+deposit bundle; everything else is the recurring/one-time price.
  const amountDueToday = plan?.enrollmentPrice && plan.frequency !== 'one_time'
    ? plan.enrollmentPrice
    : studentPrice;
  // Discount visibility: studio + course toggles + a plan that supports discounts.
  // Sub-no-advance recurring is rejected by the server, so we hide the field there.
  const planSupportsDiscount = plan && (
    plan.frequency === 'one_time' || course?.requireAdvanceDeposit !== false
  );
  const showDiscountUI = isAuthenticated &&
    studioData?.discountsEnabled &&
    course?.discountsEnabled &&
    !!selectedStudentId &&
    (selectedPlan !== null && selectedPlan !== undefined) &&
    planSupportsDiscount;
  // Local preview math — the server is authoritative on the actual charge.
  // Discount is teacher-side $X; the student saves ~$X on the upfront amount.
  const discountedAmountDueToday = appliedDiscount
    ? Math.max(0, amountDueToday - (appliedDiscount.amountOff || 0))
    : amountDueToday;
  // Selected profile shorthand
  const selectedProfile = profiles.find(p => p._id === selectedStudentId) || null;
  // If the picker is showing, suppress legacy "already enrolled" errors that
  // were raised before the picker appeared — the picker itself is the UX now.
  const displayedError = showPicker && !selectedProfile ? '' : error;
  const tz = TZ_SHORT[course.schedule?.timezone] || course.schedule?.timezone || '';
  const scheduleEntries = sortEntriesByDayOrder(getCourseScheduleEntries(course));
  const allSameTime = scheduleEntries.length > 0 && scheduleEntries.every(e =>
    e.startTime === scheduleEntries[0].startTime && e.endTime === scheduleEntries[0].endTime
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <h2 className="text-lg font-bold text-gray-900">{studioData?.name}</h2>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {paymentSuccess ? (
          /* ── Success state ── */
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="text-5xl mb-4">&#9989;</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">You're enrolled!</h1>
            <p className="text-lg text-gray-700 font-medium mb-3">{course.name}</p>

            {/* Schedule info */}
            {scheduleEntries.length > 0 && (
              <p className="text-sm text-gray-600 mb-1">
                {allSameTime ? (
                  <>
                    {scheduleEntries.map(e => e.day.charAt(0).toUpperCase() + e.day.slice(1)).join(', ')}
                    {scheduleEntries[0].startTime && (
                      <span>
                        {' \u00B7 '}
                        {scheduleEntries[0].startTime}
                        {scheduleEntries[0].endTime ? ` \u2013 ${scheduleEntries[0].endTime}` : ''}
                        {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    {scheduleEntries.map((e, i) => (
                      <span key={e.day}>
                        {i > 0 && ', '}
                        {e.day.charAt(0).toUpperCase() + e.day.slice(1)}
                        {e.startTime && ` ${e.startTime}${e.endTime ? ` \u2013 ${e.endTime}` : ''}`}
                      </span>
                    ))}
                    {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                  </>
                )}
              </p>
            )}

            {paymentProcessing ? (
              <div className="mt-4 mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-700">
                  Your bank transfer is processing. Your enrollment will be confirmed once payment clears (1-3 business days).
                </p>
              </div>
            ) : (
              <div className="mt-4 mb-6 space-y-2">
                <p className="text-sm text-gray-600">
                  You'll receive an email with your class schedule and what to expect.
                </p>
                <p className="text-sm text-gray-600">
                  Log in to your dashboard to see upcoming classes, access materials, and track your progress.
                </p>
              </div>
            )}

            <Link
              to="/student"
              className="inline-block bg-brand-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-600 transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        ) : (
          /* ── Enrollment form ── */
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

            {/* Course Info */}
            <div className="p-6">
              <h1 className="text-2xl font-bold text-gray-900">{course.name}</h1>

              {(course.danceStyle || course.levelName) && (
                <p className="text-gray-500 mt-1">
                  {course.danceStyle}{course.danceStyle && course.levelName ? ' \u00B7 ' : ''}{course.levelName}
                </p>
              )}

              {course.description && (
                <p className="text-gray-600 mt-3 text-sm leading-relaxed">{course.description}</p>
              )}

              {/* Class Details */}
              <div className="mt-5 bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                {scheduleEntries.length > 0 && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 mt-0.5">&#x1F4C5;</span>
                    <div>
                      {allSameTime ? (
                        <>
                          <div className="font-medium text-gray-900">
                            {scheduleEntries.map(e => e.day.charAt(0).toUpperCase() + e.day.slice(1)).join(', ')}
                          </div>
                          {scheduleEntries[0].startTime && (
                            <div className="text-gray-600">
                              {scheduleEntries[0].startTime}
                              {scheduleEntries[0].endTime ? ` \u2013 ${scheduleEntries[0].endTime}` : ''}
                              {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="space-y-0.5">
                          {scheduleEntries.map(e => (
                            <div key={e.day}>
                              <span className="font-medium text-gray-900">{e.day.charAt(0).toUpperCase() + e.day.slice(1)}</span>
                              {e.startTime && (
                                <span className="text-gray-600">
                                  {' '}{e.startTime}
                                  {e.endTime ? ` \u2013 ${e.endTime}` : ''}
                                  {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {course.type === 'workshop' && course.schedule?.sessionDate && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 mt-0.5">&#x1F4C5;</span>
                    <div>
                      <div className="font-medium text-gray-900">
                        {new Date(course.schedule.sessionDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                      </div>
                      {(course.schedule.sessionStartTime || course.schedule.sessionEndTime) && (
                        <div className="text-gray-600">
                          {course.schedule.sessionStartTime}
                          {course.schedule.sessionEndTime ? ` \u2013 ${course.schedule.sessionEndTime}` : ''}
                          {tz && <span className="text-gray-400 ml-1">{tz}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {course.type === 'block' && course.schedule?.blockWeeks && (
                  <div className="flex items-start gap-3">
                    <span className="text-gray-400 mt-0.5">&#x23F3;</span>
                    <div className="text-gray-600">
                      {course.schedule.blockWeeks} weeks
                      {course.schedule.blockStartDate && (
                        <span> &middot; Starts {new Date(course.schedule.blockStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <span className="text-gray-400 mt-0.5">&#x1F4CD;</span>
                  <div>
                    <div className="text-gray-900">{LOCATION_LABELS[course.location?.type] || 'In-Person'}</div>
                    {course.location?.address && (
                      <div className="text-gray-500">{course.location.address}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Plan Selector */}
            {course.billingPlans?.length > 0 && (
              <div className="border-t border-gray-100 p-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Choose a plan</h3>
                <div className="space-y-2">
                  {course.billingPlans.map((p, i) => {
                    const price = p.studentPrice || p.amount;
                    const freqLabel = { monthly: '/mo', quarterly: '/quarter', semi_annual: '/6 mo', annual: '/yr', one_time: '' };
                    const freq = freqLabel[p.frequency] || '';
                    return (
                      <label
                        key={i}
                        className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                          selectedPlan === i
                            ? 'border-brand-500 bg-brand-50/50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="radio"
                            name="plan"
                            checked={selectedPlan === i}
                            onChange={() => handlePlanChange(i)}
                            className="accent-brand-500"
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{p.name}</div>
                            <div className="text-xs text-gray-500">{p.frequency.replace('_', ' ')}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900">${price}{freq}</div>
                          <div className="text-xs text-gray-400">(inc. of all fees)</div>
                        </div>
                      </label>
                    );
                  })}
                </div>

                {/* Enrollment fee breakdown for selected plan */}
                {plan?.enrollmentPrice && plan.frequency !== 'one_time' && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">First {plan.frequency.replace('_', ' ')}</span>
                      <span className="text-gray-900">${plan.studentPrice}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Last month deposit <span className="text-xs">(prepaid, covers your final month)</span></span>
                      <span className="text-gray-900">${plan.advanceDeposit}</span>
                    </div>
                    {appliedDiscount && (
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-green-700">Discount ({appliedDiscount.code})</span>
                        <span className="text-green-700">&minus;${appliedDiscount.amountOff}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                      <span className="font-medium text-gray-700">Due today</span>
                      <span className="font-bold text-gray-900">${appliedDiscount ? Math.max(0, plan.enrollmentPrice - appliedDiscount.amountOff) : plan.enrollmentPrice}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Then ${plan.studentPrice}/{plan.frequency === 'monthly' ? 'mo' : plan.frequency.replace('_', ' ')}</p>
                    <p className="text-xs text-gray-400 mt-1">The amount shown above includes the class dakshina as well as system processing fees as charged by vendors.</p>
                  </div>
                )}

                {/* One-time plan + applied discount — show the adjusted total */}
                {plan && plan.frequency === 'one_time' && appliedDiscount && (
                  <div className="mt-4 bg-gray-50 rounded-lg p-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">{plan.name}</span>
                      <span className="text-gray-900">${studentPrice}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-green-700">Discount ({appliedDiscount.code})</span>
                      <span className="text-green-700">&minus;${appliedDiscount.amountOff}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                      <span className="font-medium text-gray-700">Due today</span>
                      <span className="font-bold text-gray-900">${discountedAmountDueToday}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">The amount shown above includes the class dakshina as well as system processing fees as charged by vendors.</p>
                  </div>
                )}
              </div>
            )}

            {/* FAQs — hidden when the AI support chat is enabled on this course
                 (the chat reads the same FAQ data plus aiKnowledge and answers
                 conversationally, so the static block is redundant). Studios
                 without chat keep the FAQ block as their Q&A surface. */}
            {!course?.supportChatEnabled && (() => {
              const applicableFaqs = (studioData?.faqs || []).filter(faq =>
                faq.appliesTo === 'all' || faq.courses?.includes(courseId)
              );
              return applicableFaqs.length > 0 && (
                <div className="border-t border-gray-100 p-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Frequently Asked Questions</h3>
                  <div className="space-y-3">
                    {applicableFaqs.map((faq, i) => (
                      <details key={i} className="group">
                        <summary className="flex items-center justify-between cursor-pointer text-sm font-medium text-gray-800 py-2 px-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                          {faq.question}
                          <svg className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                          </svg>
                        </summary>
                        <p className="text-sm text-gray-600 mt-1 px-3 pb-2 whitespace-pre-line">{faq.answer}</p>
                      </details>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Auth + Payment Section */}
            <div className="border-t border-gray-100 p-6">
              {displayedError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {displayedError}
                  <button onClick={() => setError('')} className="ml-2 text-red-500 hover:text-red-700 font-bold">&times;</button>
                </div>
              )}

              {!isAuthenticated ? (
                /* ── Not logged in ── */
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Create an account to enroll</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={handleSignUp}
                      disabled={authLoading}
                      className="flex-1 bg-brand-500 text-white py-3 rounded-lg font-semibold hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                    >
                      {authLoading ? 'Opening...' : 'Sign Up'}
                    </button>
                    <button
                      onClick={handleLogin}
                      disabled={authLoading}
                      className="flex-1 border-2 border-brand-500 text-brand-500 py-3 rounded-lg font-semibold hover:bg-brand-50 disabled:border-gray-300 disabled:text-gray-400 transition-colors"
                    >
                      Log In
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Logged in ── */
                <div>
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <div className="w-8 h-8 shrink-0 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 text-sm font-bold">
                        {(user?.name || user?.email || '?').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{user?.name || 'Student'}</div>
                        <div className="text-xs text-gray-500 truncate">{user?.email}</div>
                      </div>
                    </div>
                    <span className="shrink-0 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded font-medium whitespace-nowrap">Signed in</span>
                  </div>

                  {/* Profile picker — only shows when the account has dependents */}
                  {showPicker && (
                    <div className="mb-5">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Who is this enrollment for?</h3>
                      <div className="space-y-2">
                        {profiles.map(p => (
                          <label
                            key={p._id}
                            className={`flex items-center justify-between gap-2 p-3 rounded-lg border-2 transition-colors ${
                              p.alreadyEnrolled
                                ? 'border-gray-100 bg-gray-50 cursor-not-allowed'
                                : selectedStudentId === p._id
                                  ? 'border-brand-500 bg-brand-50/50 cursor-pointer'
                                  : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                            }`}
                          >
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <input
                                type="radio"
                                name="profile"
                                checked={selectedStudentId === p._id}
                                disabled={p.alreadyEnrolled}
                                onChange={() => handleProfileChange(p._id)}
                                className="accent-brand-500 shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {p.name}{p.isSelf && <span className="text-gray-400 font-normal"> (you)</span>}
                                </div>
                                {!p.alreadyEnrolled && p.otherEnrollments.length > 0 && (
                                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                                    Enrolled in {p.otherEnrollments.map(c => c.courseName).filter(Boolean).join(', ')}
                                  </div>
                                )}
                              </div>
                            </div>
                            {p.alreadyEnrolled && (
                              <span className="shrink-0 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                                {p.currentCourseName ? `Enrolled in ${p.currentCourseName}` : 'Already enrolled'}
                              </span>
                            )}
                          </label>
                        ))}

                        {/* Add family member — inline form */}
                        {!addingChild ? (
                          <button
                            type="button"
                            onClick={() => { setAddingChild(true); setAddingChildError(''); }}
                            className="w-full text-left p-3 rounded-lg border-2 border-dashed border-gray-300 text-sm text-gray-600 hover:border-brand-400 hover:text-brand-600 transition-colors"
                          >
                            + Add a family member
                          </button>
                        ) : (
                          <form onSubmit={handleAddChild} className="p-3 rounded-lg border-2 border-brand-200 bg-brand-50/30 space-y-2">
                            <input
                              type="text"
                              autoFocus
                              placeholder="Family member's full name"
                              value={newChildName}
                              onChange={(e) => setNewChildName(e.target.value)}
                              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-400"
                              disabled={addingChildSaving}
                            />
                            {addingChildError && (
                              <div className="text-xs text-red-600">{addingChildError}</div>
                            )}
                            <div className="flex gap-2">
                              <button
                                type="submit"
                                disabled={addingChildSaving}
                                className="flex-1 bg-brand-500 text-white py-2 rounded text-sm font-semibold hover:bg-brand-600 disabled:bg-gray-300"
                              >
                                {addingChildSaving ? 'Adding...' : 'Add'}
                              </button>
                              <button
                                type="button"
                                onClick={() => { setAddingChild(false); setNewChildName(''); setAddingChildError(''); }}
                                className="flex-1 border border-gray-300 text-gray-600 py-2 rounded text-sm font-semibold hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    </div>
                  )}

                  {/* When the only eligible profile is already enrolled, prompt the user
                      to add a family member. Skip the payment UI entirely. */}
                  {showPicker && !selectedProfile && (
                    <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                      Add a family member above to continue enrolling in this course.
                    </div>
                  )}

                  {/* Plan-not-picked hint. Shown when the user is signed in, has an
                      eligible profile, but hasn't picked a plan yet on a multi-plan course. */}
                  {selectedProfile && !selectedProfile.alreadyEnrolled && (selectedPlan === null || selectedPlan === undefined) && course?.billingPlans?.length > 1 && (
                    <div className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                      Choose a plan above to continue.
                    </div>
                  )}

                  {/* Discount-code accordion */}
                  {showDiscountUI && !paymentSuccess && (
                    <div className="mb-5">
                      {appliedDiscount ? (
                        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-green-200 bg-green-50/70">
                          <div className="text-sm text-green-800 min-w-0 flex-1 truncate">
                            <span className="font-semibold font-mono">{appliedDiscount.code}</span>
                            <span className="ml-2">applied &mdash; ${appliedDiscount.amountOff} off</span>
                          </div>
                          <button
                            type="button"
                            onClick={handleRemoveDiscount}
                            className="shrink-0 text-xs text-green-700 hover:text-green-900 underline"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="border border-gray-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setDiscountAccordionOpen(o => !o)}
                            className="w-full flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 transition-colors"
                          >
                            <span>Have a discount code?</span>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${discountAccordionOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                            </svg>
                          </button>
                          {discountAccordionOpen && (
                            <div className="p-4 border-t border-gray-200">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={discountInput}
                                  onChange={e => { setDiscountInput(e.target.value.toUpperCase()); setDiscountError(''); }}
                                  placeholder="Enter code"
                                  maxLength={20}
                                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyDiscount(); } }}
                                />
                                <button
                                  type="button"
                                  onClick={handleApplyDiscount}
                                  disabled={discountApplying || !discountInput.trim()}
                                  className="bg-brand-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300"
                                >
                                  {discountApplying ? 'Checking...' : 'Apply'}
                                </button>
                              </div>
                              {discountError && (
                                <div className="mt-2 text-sm text-red-600">{discountError}</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* One-click enroll using saved payment method */}
                  {selectedProfile && !selectedProfile.alreadyEnrolled && selectedPlan !== null && selectedPlan !== undefined && savedPaymentMethod && useSavedPm && !clientSecret && !paymentSuccess && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment method</h3>
                      <div className="flex items-center justify-between gap-3 p-4 rounded-lg border-2 border-brand-200 bg-brand-50/40 mb-3">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          <div className="w-10 h-10 shrink-0 rounded bg-white border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                            {savedPaymentMethod.type === 'bank' ? 'ACH' : (savedPaymentMethod.brand || 'CARD').toString().slice(0, 4).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {savedPaymentMethod.type === 'bank' ? 'Bank' : (savedPaymentMethod.brand || 'Card')} &bull;&bull;&bull;&bull; {savedPaymentMethod.last4}
                            </div>
                            <div className="text-xs text-gray-500 truncate">Saved from a previous enrollment</div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setUseSavedPm(false)}
                          className="shrink-0 text-xs text-brand-600 hover:underline whitespace-nowrap"
                        >
                          Use a different card
                        </button>
                      </div>
                      {tcsRequired && (
                        <TCsCheckboxRow
                          studioName={studioData?.name}
                          accepted={tcsAccepted}
                          onChange={setTcsAccepted}
                          onView={() => setTcsModalOpen(true)}
                        />
                      )}
                      <button
                        type="button"
                        onClick={handleOneClickEnroll}
                        disabled={oneClickEnrolling || !selectedStudentId || (tcsRequired && !tcsAccepted)}
                        className="w-full bg-brand-500 text-white py-3.5 rounded-lg text-lg font-semibold hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {oneClickEnrolling ? 'Enrolling...' : `Enroll & Pay $${discountedAmountDueToday}`}
                      </button>
                      {tcsRequired && !tcsAccepted && (
                        <p className="mt-2 text-xs text-amber-700 text-center">Please agree to the Terms &amp; Conditions above to enable payment.</p>
                      )}
                      {error && (
                        <div className="mt-3 text-sm text-red-600">{error}</div>
                      )}
                    </div>
                  )}

                  {/* Pre-payment T&Cs gate (new-card path). The Elements form below
                      only renders once clientSecret exists, and PI creation is gated
                      on tcsAccepted — so without this block the checkbox would never
                      appear and the user couldn't proceed. */}
                  {tcsRequired && !tcsAccepted && selectedProfile && !selectedProfile.alreadyEnrolled && selectedPlan !== null && selectedPlan !== undefined && (!savedPaymentMethod || !useSavedPm) && !clientSecret && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment method</h3>
                      <TCsCheckboxRow
                        studioName={studioData?.name}
                        accepted={tcsAccepted}
                        onChange={setTcsAccepted}
                        onView={() => setTcsModalOpen(true)}
                      />
                      <p className="mt-3 text-xs text-gray-500 text-center">Once you agree, we'll load your payment options.</p>
                    </div>
                  )}

                  {/* Payment Form — shown when no saved PM, user opted into new card, or one-click failed */}
                  {creatingPayment && selectedProfile && !selectedProfile.alreadyEnrolled && selectedPlan !== null && selectedPlan !== undefined && (!savedPaymentMethod || !useSavedPm) && (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-sm">Setting up payment...</div>
                    </div>
                  )}

                  {clientSecret && selectedProfile && !selectedProfile.alreadyEnrolled && selectedPlan !== null && selectedPlan !== undefined && (!savedPaymentMethod || !useSavedPm) && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment method</h3>
                      {tcsRequired && (
                        <TCsCheckboxRow
                          studioName={studioData?.name}
                          accepted={tcsAccepted}
                          onChange={setTcsAccepted}
                          onView={() => setTcsModalOpen(true)}
                        />
                      )}
                      <Elements
                        stripe={stripePromise}
                        options={{ clientSecret, appearance: STRIPE_APPEARANCE }}
                      >
                        <CheckoutForm
                          enrollmentId={enrollmentId}
                          totalAmount={isEnrollmentWithAdvance ? enrollmentTotal : discountedAmountDueToday}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                          tcsRequired={tcsRequired}
                          tcsAccepted={tcsAccepted}
                        />
                      </Elements>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <p className="text-center text-xs text-gray-400 mt-6">
          Powered by DanceHall Studios
        </p>
      </div>
      <SupportChat studio={studioData} course={course} />
      <TCsModal
        open={tcsModalOpen}
        onClose={() => setTcsModalOpen(false)}
        studioName={studioData?.name}
        tcs={studioData?.tcs}
      />
    </div>
  );
};

export default EnrollmentPage;
